import React from "react";
import { styled } from "@mui/material/styles";
import { MenuItem, Popper, Paper, ClickAwayListener, outlinedInputClasses, useTheme, inputBaseClasses, menuItemClasses } from "@mui/material";
import ClockIcon from "../../../../icons/ClockIcon";
import { StyledTextField } from "@hyro-components/fields/text-field/HyroTextField.styles";
import useHyroTimePicker from "./TimePickerField.hook";
import { TIME_PICKER_STYLE } from "../HyroTimePicker.consts";

const TimePickerField = ({
  value,
  onChange,
  layoutStyle,
  disabled = false,
  error,
  helperText,
  placeholder = "Time",
  fromTime = null,
  testId,
  minutesStep = 15,
  timeConstraints,
  fullWidth = false,
}) => {
  const theme = useTheme();

  const {
    anchorEl,
    isOpen,
    isEditMode,
    displayValue,
    timeOptions,
    handleClick,
    handleClose,
    handleTimeSelect,
    handleInputChange,
    handleInputBlur,
    handleInputKeyDown,
    getIsSelected,
    handleSelectedRef
  } = useHyroTimePicker({
    value,
    onChange,
    fromTime,
    minutesStep,
    timeConstraints
  });

  return (
    <>
      <StyledTextField
        variant="outlined"
        value={displayValue}
        onClick={disabled ? undefined : handleClick}
        onChange={isEditMode ? handleInputChange : undefined}
        onBlur={isEditMode ? handleInputBlur : undefined}
        onKeyDown={isEditMode ? handleInputKeyDown : undefined}
        disabled={disabled}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        isFieldReadOnlyFromSpec={disabled && layoutStyle === TIME_PICKER_STYLE.NARROW}
        inputProps={{
          "data-testid": `${testId}-input${error ? "-error" : ""}`,
        }}
        sx={{
          maxWidth: fullWidth ? "100%" : (layoutStyle === TIME_PICKER_STYLE.WIDE ? 160 : 140),
          width: fullWidth ? "100%" : undefined,
          [`& .${outlinedInputClasses.root}`]: {
            minHeight: layoutStyle === TIME_PICKER_STYLE.WIDE ? 42 : 48,
            height: layoutStyle === TIME_PICKER_STYLE.WIDE ? 42 : 48
          }
        }}
        InputProps={{
          readOnly: !isEditMode,
          sx: {
            [`&, & .${outlinedInputClasses.input}, & .${outlinedInputClasses.root}.${inputBaseClasses.root}`]: {
              cursor: disabled ? "default" : (isEditMode ? "text" : "pointer"),
              paddingRight: theme => layoutStyle === TIME_PICKER_STYLE.WIDE ? theme.spacing(1.5) : theme.spacing(1.75)
            },
            [`& .${outlinedInputClasses.notchedOutline}`]: {
              border: (theme) => `1px solid ${theme.palette.grey[300]}`,
            },
          },
          endAdornment: <ClockIcon style={{ flexShrink: 0 }} size={layoutStyle === TIME_PICKER_STYLE.WIDE ? 20 : 24} color={theme.palette.system[700]} />,
        }}
      />
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
        sx={{ minWidth: anchorEl?.offsetWidth, zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <TimeDropdown sx={{ mt: 0, p: 1 }}>
            {timeOptions.map((option) => {
              const isSelected = getIsSelected(option);
              return (
                <MenuItem
                  sx={{ height: 44, margin: theme => theme.spacing(0, 0, 0.5, 0), [`&.${menuItemClasses.root}.${menuItemClasses.selected}`]: { color: theme.palette.text.primary } }}
                  key={option.value.format("HH:mm")}
                  onClick={() => handleTimeSelect(option)}
                  selected={isSelected}
                  ref={isSelected ? handleSelectedRef : undefined}
                  data-testid={`${testId}-option-${option.value.format("HH:mm")}${option.duration ? ` (${option.formattedDuration})` : ""}`}
                >
                  {option.label}
                </MenuItem>
              );
            })}
          </TimeDropdown>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

const TimeDropdown = styled(Paper)(({ theme }) => ({
  maxHeight: "250px",
  overflow: "auto",
  marginTop: theme.spacing(.5),
  boxShadow: theme.shadows[4],
  [`& .${menuItemClasses.root}`]: {
    fontSize: "14px",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: theme.palette.system[50],
    },
    [`&.${menuItemClasses.selected}`]: {
      backgroundColor: theme.palette.primary[50],
      color: theme.palette.primary[500],
    },
  },
}));

export default TimePickerField;
